import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, posterUrl } from "../lib/api";
import { Film, Play, ArrowLeft, Tv } from "lucide-react";

export default function SeriesDetail() {
  const { id } = useParams();
  const [series, setSeries] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [season, setSeason] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/series/${id}`).then(({ data }) => {
      setSeries(data.series);
      setEpisodes(data.episodes || []);
      const seasons = [...new Set((data.episodes || []).map((e) => e.season || 1))].sort();
      if (seasons.length) setSeason(seasons[0]);
    }).catch(() => setSeries(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-[#E65F2B] border-t-transparent animate-spin" /></div>;
  }
  if (!series) {
    return <div className="max-w-[1100px] mx-auto px-6 py-24 text-center" data-testid="series-not-found">
      <Tv className="h-12 w-12 mx-auto text-white/20 mb-4" />
      <h3 className="font-display text-2xl">Series not found</h3>
      <Link to="/browse" className="ember-btn mt-4 inline-flex">Back to library</Link>
    </div>;
  }

  const seasonsSet = [...new Set(episodes.map((e) => e.season || 1))].sort();
  const inSeason = episodes
    .filter((e) => (e.season || 1) === season)
    .sort((a, b) => (a.episode || 0) - (b.episode || 0));

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12" data-testid="series-detail-page">
      <Link to="/browse" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-6" data-testid="series-back-link">
        <ArrowLeft className="h-4 w-4" /> Library
      </Link>

      <div className="card-surface p-8 sm:p-10 mb-10">
        <div className="flex items-center gap-2 mb-3">
          <Tv className="h-4 w-4 text-[#E65F2B]" />
          <p className="text-eyebrow text-[#E65F2B]">Series · {series.genre}</p>
        </div>
        <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter" data-testid="series-title">
          {series.title}
        </h1>
        {series.description && (
          <p className="text-white/60 mt-4 max-w-3xl">{series.description}</p>
        )}
        <p className="text-xs text-white/40 mt-3">
          {episodes.length} {episodes.length === 1 ? "episode" : "episodes"} available
        </p>
      </div>

      {seasonsSet.length > 1 && (
        <div className="flex gap-2 mb-6 overflow-x-auto row-scroll" data-testid="season-selector">
          {seasonsSet.map((s) => (
            <button
              key={s}
              onClick={() => setSeason(s)}
              data-testid={`season-tab-${s}`}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all border ${
                season === s ? "bg-[#E65F2B] border-[#E65F2B] text-white" : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"
              }`}
            >
              Season {s}
            </button>
          ))}
        </div>
      )}

      {inSeason.length === 0 ? (
        <div className="card-surface p-12 text-center text-white/50" data-testid="series-no-episodes">
          <Film className="h-8 w-8 mx-auto text-white/20 mb-3" />
          No episodes uploaded yet.
        </div>
      ) : (
        <div className="space-y-4" data-testid="episodes-list">
          {inSeason.map((e) => (
            <Link
              to={`/movie/${e.id}`}
              key={e.id}
              className="card-surface p-4 flex flex-col sm:flex-row gap-4 items-center hover:border-[#E65F2B]/30 transition-colors group"
              data-testid={`episode-${e.id}`}
            >
              <div className="relative w-full sm:w-48 aspect-video rounded-lg overflow-hidden bg-[#141414] shrink-0">
                <img
                  src={posterUrl(e.id)}
                  alt={e.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={(ev) => { ev.currentTarget.style.display = "none"; }}
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="h-12 w-12 rounded-full bg-[#E65F2B] flex items-center justify-center">
                    <Play className="h-5 w-5 text-white fill-white ml-0.5" />
                  </div>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#E65F2B] uppercase tracking-wider mb-1">
                  S{e.season || 1} · E{e.episode || 1}
                </p>
                <h3 className="font-display font-bold text-lg leading-tight">
                  {e.episode_title || e.title}
                </h3>
                {e.description && (
                  <p className="text-sm text-white/50 mt-2 line-clamp-2">{e.description}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
